import { API } from '@/api';
import { fetcher, HTTPError } from '@/services/fetcher';
import { toURLSearchParams } from '@/utils/url';

import {
  type ContractDocumentsParams,
  type ContractHistoryParams,
  type ContractHistorySearchApi,
  type ContractPricingParams,
  type ContractPricingRaw,
  contractDocumentsApiSchema,
  contractHistorySearchApiSchema,
  contractPricingSearchApiSchema,
  type PricingHeaderApi,
  pricingHeaderApiSchema,
} from '../models';
import { logger } from '../utils/logger';

const pricing = fetcher.extend(() => ({ prefixUrl: `${API.BASE_PRICING}` }));

const fetchContractPricingSearch = async (
  params: ContractPricingParams,
  { signal }: { signal?: AbortController['signal'] } = {},
) => {
  const { contractKey, limit = 10, offset = 0, pricingId, sorting } = params;

  return pricing
    .get('contract/contract-pricing/search', {
      searchParams: toURLSearchParams({
        ContractKey: contractKey,
        PricingId: pricingId,
        Skip: offset,
        Sorting: sorting || undefined,
        Take: limit,
      }),
      signal,
    })
    .json(contractPricingSearchApiSchema);
};

export const fetchPricingHeader = async (
  pricingKey: number,
  { signal }: { signal?: AbortController['signal'] } = {},
): Promise<PricingHeaderApi> => {
  try {
    return await pricing.get(`pricing/${pricingKey}`, { signal }).json(pricingHeaderApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Pricing header error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchContractHistory = async (
  params: ContractHistoryParams,
  { signal }: { signal?: AbortController['signal'] } = {},
): Promise<ContractHistorySearchApi> => {
  const { contractKey, limit = 10, offset = 0, sorting } = params;

  try {
    return await pricing
      .get(`contract/${contractKey}/history`, {
        searchParams: toURLSearchParams({
          Skip: offset,
          Sorting: sorting || undefined,
          Take: limit,
        }),
        signal,
      })
      .json(contractHistorySearchApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Contract history error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchContractDocuments = async (
  params: ContractDocumentsParams,
  { signal }: { signal?: AbortController['signal'] } = {},
) => {
  const { comment, contractKey, limit = 5, name, offset = 0, sorting, type } = params;

  try {
    return await pricing
      .get('contract/documents/search', {
        searchParams: toURLSearchParams({
          Comment: comment,
          ContractKey: contractKey,
          Name: name,
          Skip: offset,
          Sorting: sorting || undefined,
          Take: limit,
          Type: type,
        }),
        signal,
      })
      .json(contractDocumentsApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Contract documents error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchContractPricing = async (
  params: ContractPricingParams,
  { signal }: { signal?: AbortController['signal'] } = {},
): Promise<ContractPricingRaw> => {
  try {
    const search = await fetchContractPricingSearch(params, { signal });

    const items = await Promise.all(
      search.items.map(async (link) => ({
        key: link.key,
        pricing: await fetchPricingHeader(link.pricingId, { signal }),
      })),
    );

    return {
      items,
      totalCount: search.totalCount,
    };
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Contract pricing error: ${error.response.status}`);
    }
    throw error;
  }
};
